import { useFirestore } from "@/hooks/firestore/core/useFirestore";
import {
  racingEvent,
  type RacingEvent,
} from "@/hooks/firestore/models/racingEvent";
import { useMemo } from "react";
import { useCampaigns } from "@/hooks/firestore/user/useCampaigns";
import {
  processRacingEvent,
  regionMap,
} from "@/hooks/firestore/betting/shared";
import { useTokens } from "@/hooks/firestore/user/useTokens";
import { usePermissions } from "@/features/auth/hooks/usePermission";
import {
  limit,
  orderBy,
  type QueryConstraint,
  where,
} from "firebase/firestore";
import { useUserEventFilters } from "@/hooks/firestore/user/useUserEventFilters";
import type { Always, NullableMaybe } from "@/types/utilities";
import { racingCompetitor } from "@/hooks/firestore/models/racingCompetitor";
import type { ProcessedBettingCompetitor } from "@/hooks/firestore/betting/useEvent";
import type { PickemsCompetitor } from "../models/pickems";

type RacingEventOptions = {
  titles?: string[];
  page?: number;
  pageSize?: number;
  regions?: ("au" | "world")[];
  meetingId?: string;
  status?: RacingEvent["status"];
};

const DEFAULT_PAGE_SIZE = 50;

const getRacingEventsQuery = ({
  titles,
  page,
  pageSize = DEFAULT_PAGE_SIZE,
  regions,
  eventFilters,
  meetingId,
  status,
}: RacingEventOptions & {
  eventFilters: string[];
}) => {
  const queries: QueryConstraint[] = [where("visible", "==", true)];
  const safeRegions = (regions || []).map((region) => regionMap[region]);

  if (status) {
    queries.push(where("status", "==", status));
  }

  if (safeRegions.length > 0) {
    queries.push(where("region", "in", safeRegions));
  }

  if (titles && titles.length > 0) {
    queries.push(
      where(
        "sport",
        "in",
        titles.map((title) => title.toUpperCase()),
      ),
    );
  }

  if (eventFilters?.length > 0) {
    queries.push(where("filters", "array-contains-any", eventFilters));
  }

  queries.push(orderBy("scheduledStartTimeTs", "asc"));

  if (page) {
    queries.push(limit(pageSize * page));
  }

  if (meetingId) {
    queries.push(where("meetingId", "==", meetingId));
  }

  return queries;
};

const useRacingEvents = ({
  regions,
  titles,
  page,
  pageSize = DEFAULT_PAGE_SIZE,
  status,
  meetingId,
}: RacingEventOptions) => {
  const eventFilters = useUserEventFilters();
  const {
    data: events,
    error,
    isLoading: eventsLoading,
    isLagging,
  } = useFirestore(`/racingEvents`, racingEvent.array(), {
    query: getRacingEventsQuery({
      titles,
      regions,
      page,
      pageSize,
      eventFilters,
      status,
      meetingId,
    }),
    keyPath: "id",
    // cache only has the previous page, wait for the full list
    getFromServer: (page ?? 1) > 1,
  });

  const { tokens } = useTokens();
  const { codes, isLoading: codesLoading } = useCampaigns();
  const permissions = usePermissions();

  const processedEvents = useMemo(() => {
    if (!events || !codes) return;

    return events.map((event) =>
      processRacingEvent(event, codes, tokens, permissions),
    );
  }, [events, codes, tokens, permissions]);

  return {
    events: processedEvents,
    error,
    isLoading: eventsLoading || codesLoading,
    hasMore: isLagging || (events?.length ?? 0) >= (page ?? 1) * pageSize,
    isFetchingMore: isLagging,
  };
};

const useRacingEvent = (raceId: NullableMaybe<string>) => {
  const {
    data,
    isLoading: eventLoading,
    error,
  } = useFirestore(raceId ? `/racingEvents/${raceId}` : null, racingEvent, {
    keyPath: `id`,
  });

  const { tokens } = useTokens();
  const { codes, isLoading: codesLoading } = useCampaigns();
  const permissions = usePermissions();
  const isLoading = eventLoading || codesLoading;

  const event = useMemo(() => {
    if (!data || !codes) return;

    return processRacingEvent(data, codes, tokens, permissions);
  }, [data, codes, tokens, permissions]);

  return { event, isLoading, error };
};

const useRacingEventCompetitors = (eventId: NullableMaybe<string>) => {
  const { data, error, isLoading } = useFirestore(
    eventId && `/racingEvents/${eventId}/competitors`,
    racingCompetitor.array(),
    {
      keyPath: "id",
    },
  );

  return {
    competitors: data,
    error,
    isLoading,
  };
};

const isRacingCompetitor = (
  competitor:
    | ProcessedRacingCompetitor
    | ProcessedBettingCompetitor
    | PickemsCompetitor,
): competitor is ProcessedRacingCompetitor => {
  return "number" in competitor;
};

type ProcessedRacingEvent = Always<
  ReturnType<typeof useRacingEvents>["events"]
>[0];

type ProcessedRacingCompetitors = Always<
  ReturnType<typeof useRacingEventCompetitors>["competitors"]
>;

type ProcessedRacingCompetitor = ProcessedRacingCompetitors[number];

export type {
  ProcessedRacingEvent,
  ProcessedRacingCompetitors,
  ProcessedRacingCompetitor,
};
export {
  useRacingEvents,
  useRacingEvent,
  useRacingEventCompetitors,
  isRacingCompetitor,
};
